import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use-v13';
import { TableColumn } from '@scale/scaleui';

interface ColumnOrder {
  columnId: string;
  ordering: number;
}

type ColumnHiddenMap = { [columnId: string]: boolean };

export function useTableColumnDisplayOrder(queryKey: string, columns: TableColumn[]) {
  const orderingKey = `ScaleCorp:TableColumnOrdering:${queryKey}`;
  const visibilityKey = `ScaleCorp:TableColumnVisibility:${queryKey}`;

  const [columnOrdering, setColumnOrdering] = useLocalStorage<ColumnOrder[] | null>(orderingKey);
  const [columnHiddenMap, setColumnHiddenMap] = useLocalStorage<ColumnHiddenMap | null>(
    visibilityKey,
  );

  const reset = useCallback(() => {
    setColumnOrdering(null);
    setColumnHiddenMap({});
  }, [setColumnOrdering, setColumnHiddenMap]);

  const orderedColumns = useMemo(() => {
    if (!columnOrdering) {
      return columns;
    }
    const ordering = _.keyBy(columnOrdering, 'columnId');
    // By default, puts new columns (from the code) in front of the ordering
    return _.sortBy(columns, column => ordering[column.id]?.ordering ?? 1);
  }, [columns, columnOrdering]);

  const displayColumns = useMemo(() => {
    return orderedColumns.filter(column => !columnHiddenMap?.[column.id]);
  }, [orderedColumns, columnHiddenMap]);

  const toggleVisibility = useCallback(
    (columnId: string) => {
      setColumnHiddenMap((map: ColumnHiddenMap | null) => {
        const newMap = { ...map };
        newMap[columnId] = !newMap[columnId];
        return newMap;
      });
    },
    [setColumnHiddenMap],
  );

  const setColumnOrderingFromList = useCallback(
    (columnIds: string[]) => {
      setColumnOrdering(
        columnIds.map((columnId, index) => ({
          columnId,
          ordering: index,
        })),
      );
    },
    [setColumnOrdering],
  );

  const isColumnVisible = useCallback(
    (columnId: string) => !columnHiddenMap?.[columnId],
    [columnHiddenMap],
  );

  const showOnlyColumns = useCallback(
    (visibleColumns: string[]) => {
      const pairs = columns.map(column => [column.id, !visibleColumns.includes(column.id)]);
      const hiddenMap = _.fromPairs(pairs);
      setColumnHiddenMap(hiddenMap);
    },
    [setColumnHiddenMap, columns],
  );

  return {
    queryKey,
    reset,
    orderedColumns,
    displayColumns,
    setColumnOrderingFromList,
    isColumnVisible,
    toggleVisibility,
    showOnlyColumns,
  };
}
